import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { EmailCaptureConfig } from '@/types/vsleads/emailCaptureTypes';
import { getDefaultEmailCaptureConfig } from '@/utils/vsleads/emailCaptureDefaults';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { EmailCapturePreview } from './EmailCapturePreview';
import { ContentTab } from './tabs/ContentTab';
import { LayoutConfigTab } from './tabs/LayoutConfigTab';
import { FormFieldsTab } from './tabs/FormFieldsTab';
import { VisualStyleTab } from './tabs/VisualStyleTab';
import { TimingTab } from './tabs/TimingTab';
import { useToast } from '@/hooks/use-toast';
import { Save } from 'lucide-react';
import { videoService } from '@/services/videoService';
import { vsleadsService } from '@/services/vsleadsService';

interface EmailCaptureEditorProps {
  isEditMode?: boolean;
  isCapturePassword?: boolean;
}

export function EmailCaptureEditor({ isEditMode = false, isCapturePassword = false }: EmailCaptureEditorProps) {
  useLanguage();
  const [activeTab, setActiveTab] = useState('content');
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentVideoId, setCurrentVideoId] = useState<string | null>(null);

  // Determinar a tab correta para redirecionamento
  const redirectTab = isCapturePassword ? 'captura-senha' : 'captura-email';
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  
  const form = useForm<EmailCaptureConfig>({
    defaultValues: getDefaultEmailCaptureConfig(),
  });

  const config = form.watch();
  const watchedVideoId = form.watch('videoId');

  // Atualizar currentVideoId quando videoId do formulário mudar (modo criação)
  useEffect(() => {
    if (watchedVideoId && !isEditMode) {
      setCurrentVideoId(watchedVideoId);
    }
  }, [watchedVideoId, isEditMode]);

  // Buscar URL do vídeo quando videoId mudar (modo criação)
  useEffect(() => {
    const loadVideoUrl = async () => {
      if (!watchedVideoId || isEditMode) {
        return;
      }

      try {
        const response = await videoService.getVideoById(watchedVideoId);

        // A API pode retornar {response: {...}} ou {response: [{...}]}
        let videoData = null;
        if (response?.response) {
          if (Array.isArray(response.response)) {
            videoData = response.response[0];
          } else {
            videoData = response.response; // Se não é array, pega direto
          }
        } else if (Array.isArray(response)) {
          videoData = response[0];
        } else {
          videoData = response?.data || response;
        }

        // Priorizar videoMP4 que é o campo correto
        const foundVideoUrl = videoData?.videoMP4 || videoData?.videoDownload || videoData?.video_download || videoData?.videoUrl || videoData?.video;

        if (foundVideoUrl) {
          setVideoUrl(foundVideoUrl);
        }
      } catch (error) {
        console.error('Erro ao carregar vídeo:', error);
      }
    };

    loadVideoUrl();
  }, [watchedVideoId, isEditMode]);

  // Buscar dados do vídeo quando em modo de edição
  useEffect(() => {
    const loadVideoData = async () => {
      if (!id || !isEditMode) {
        return;
      }

      setIsLoading(true);
      try {
        // Buscar dados do vídeo
        const response = await videoService.getVideoById(id);

        // A API pode retornar {response: {...}} ou {response: [{...}]}
        let videoData = null;
        if (response?.response) {
          if (Array.isArray(response.response)) {
            videoData = response.response[0];
          } else {
            videoData = response.response; // Se não é array, pega direto
          }
        } else if (Array.isArray(response)) {
          videoData = response[0];
        } else {
          videoData = response?.data || response;
        }

        // Priorizar videoMP4 que é o campo correto
        const foundVideoUrl = videoData?.videoMP4 || videoData?.videoDownload || videoData?.video_download || videoData?.videoUrl || videoData?.video;

        if (foundVideoUrl) {
          setVideoUrl(foundVideoUrl);
        }

        // Mapear dados do vídeo para o formato do formulário
        if (videoData) {
          // Usar o campo haveForm do backend para determinar se está ativo
          const isActive = videoData.haveForm === true;

          // Armazenar o videoId correto
          const videoId = videoData.id_video || videoData.id || id;
          setCurrentVideoId(videoId);

          const formData: Partial<EmailCaptureConfig> = {
            videoId: videoId,
            isActive: isActive,
          };

          // Mapear campos do formulário - SEMPRE criar os 3 campos
          const fields = [
            {
              id: 'nome',
              type: 'name' as const,
              label: 'Nome',
              placeholder: 'Digite seu nome',
              required: true,
              active: videoData.haveInputName || false,
            },
            {
              id: 'email',
              type: 'email' as const,
              label: 'E-mail',
              placeholder: 'Digite seu e-mail',
              required: true,
              active: videoData.haveInputEmail || false,
            },
            {
              id: 'whatsapp',
              type: 'phone' as const,
              label: 'WhatsApp',
              placeholder: 'Digite seu WhatsApp',
              required: true,
              active: videoData.haveInputWhatsapp || false,
            },
          ];

          formData.fields = fields;

          // Mapear textos - sempre mapear para priorizar dados do backend
          const defaultConfig = getDefaultEmailCaptureConfig();
          formData.texts = {
            headline: videoData.captureTitle || defaultConfig.texts.headline,
            // Para captura por senha, o campo footer armazena a senha
            footer: isCapturePassword ? (videoData.password || '') : (videoData.captureAuxText || defaultConfig.texts.footer),
            buttonText: videoData.captureButtonText || defaultConfig.texts.buttonText,
            skipEnabled: videoData.skipLead !== undefined ? videoData.skipLead : defaultConfig.texts.skipEnabled,
          };

          // Mapear timer - sempre mapear para priorizar dados do backend
          if (videoData.checkedCaptureTimer && videoData.haveTime !== undefined) {
            const minutes = Math.floor(videoData.haveTime / 60);
            const seconds = videoData.haveTime % 60;
            const timeString = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

            formData.timer = {
              enabled: videoData.checkedCaptureTimer,
              moment: timeString,
              pauseOnOpen: true,
              pauseAfterSubmit: false,
              blockClose: false,
            };
          } else {
            // Se não houver timer configurado no backend, usar defaults
            formData.timer = defaultConfig.timer;
          }

          // Mapear cores e estilos - sempre mapear para manter dados do backend
          formData.style = {
            container: {
              ...defaultConfig.style.container,
              background: {
                value: videoData.captureBackgroundColor || defaultConfig.style.container.background.value,
                opacity: 0.95,
              },
            },
            button: {
              ...defaultConfig.style.button,
              normal: videoData.captureButtonColor || defaultConfig.style.button.normal,
              text: videoData.captureButtonTextColor || defaultConfig.style.button.text,
            },
            text: {
              title: videoData.captureTitleColor || defaultConfig.style.text.title,
              footer: videoData.captureAuxTextColor || defaultConfig.style.text.footer,
            },
            fields: {
              ...defaultConfig.style.fields,
              background: videoData.captureInputColor || defaultConfig.style.fields.background,
            },
            overlay: {
              darkness: videoData.haveBackgroundColored ? 0.7 : 0,
              blur: 0,
            },
          };

          // Mapear orientação
          if (videoData.checkedCaptureVertical !== undefined) {
            formData.orientation = videoData.checkedCaptureVertical ? 'vertical' : 'horizontal';
          }

          // Resetar form com novos valores
          form.reset({ ...getDefaultEmailCaptureConfig(), ...formData });
        }

        toast({
          title: 'Dados carregados',
          description: 'Os dados do vídeo foram carregados com sucesso.',
        });
      } catch (error) {
        console.error('Erro ao carregar dados do vídeo:', error);
        toast({
          title: 'Erro ao carregar',
          description: 'Não foi possível carregar os dados do vídeo.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadVideoData();
  }, [id, isEditMode]);

  const handleSave = async (data: EmailCaptureConfig) => {
    // Verificar se temos um videoId válido
    const videoIdToUse = currentVideoId || data.videoId || id;

    if (!videoIdToUse) {
      toast({
        title: translateInterfaceText("Erro ao salvar"),
        description: translateInterfaceText("ID do vídeo não encontrado."),
        variant: 'destructive',
      });
      return;
    }

    try {
      // Converter dados do formulário para formato do backend
      const [minutes, seconds] = data.timer.moment.split(':').map(Number);
      const haveTime = minutes * 60 + seconds;

      // Usar o valor do toggle "Estratégia de Captura"
      const isFormActive = data.isActive;

      // Se o recurso estiver DESATIVADO, enviar campos limpos
      if (!isFormActive) {
        const cleanData = {
          haveForm: false,
          typeForm: '',

          // Campos booleanos
          haveInputName: null,
          haveInputEmail: null,
          haveInputWhatsapp: null,
          haveCaptureAuxText: null,
          checkedCaptureTimer: null,
          haveBackgroundColored: null,
          skipLead: null,
          haveCaptureBigTitle: null,

          // Textos
          captureTitle: '',
          captureButtonText: '',
          captureAuxText: '',
          password: '',

          // Cores
          captureTitleColor: '',
          captureAuxTextColor: '',
          captureButtonColor: '',
          captureButtonTextColor: '',
          captureInputColor: '',
          captureBackgroundColor: '',

          // Timer
          haveTime: null,

          // Layout
          checkedCaptureVertical: null,
        };

        console.log('Desativando recurso - enviando dados limpos:', cleanData);

        // Chamar API apropriada baseada no tipo
        if (isCapturePassword) {
          await vsleadsService.updatePasswordCapture(videoIdToUse, cleanData);
        } else {
          await vsleadsService.updateDataCapture(videoIdToUse, cleanData);
        }

        toast({
          title: translateInterfaceText("Recurso desativado!"),
          description: translateInterfaceText("O recurso foi desativado com sucesso."),
        });

        navigate(`/vsleads?tab=${redirectTab}`);
        return;
      }

      // Se o recurso estiver ATIVADO, enviar dados completos
      const backendData = {
        // Ativação
        haveForm: isFormActive,
        typeForm: isCapturePassword ? 'capture-password' : 'data-capture',

        // Senha (somente para captura por senha)
        ...(isCapturePassword && { password: data.texts.footer }),

        // Campos de formulário (não usados em senha)
        ...(!isCapturePassword && {
          haveInputName: data.fields.find(f => f.id === 'nome')?.active || false,
          haveInputEmail: data.fields.find(f => f.id === 'email')?.active || false,
          haveInputWhatsapp: data.fields.find(f => f.id === 'whatsapp')?.active || false,
        }),

        // Textos
        captureTitle: data.texts.headline,
        captureButtonText: data.texts.buttonText,
        captureAuxText: isCapturePassword ? '' : data.texts.footer,
        haveCaptureAuxText: isCapturePassword ? false : !!data.texts.footer,

        // Cores
        captureTitleColor: data.style.text.title,
        captureAuxTextColor: data.style.text.footer,
        captureButtonColor: data.style.button.normal,
        captureButtonTextColor: data.style.button.text,
        captureInputColor: data.style.fields.background,
        captureBackgroundColor: data.style.container.background.value,

        // Configurações de timer (não usado em senha)
        ...(!isCapturePassword && {
          checkedCaptureTimer: data.timer.enabled,
          haveTime: haveTime,
        }),

        // Configurações de layout
        checkedCaptureVertical: !isCapturePassword && data.orientation === 'vertical',
        haveBackgroundColored: data.style.overlay.darkness > 0,

        // Outras configurações
        ...(!isCapturePassword && { skipLead: data.texts.skipEnabled }),
        haveCaptureBigTitle: false,
      };

      console.log('Salvando configuração:', backendData);

      // Chamar API apropriada baseada no tipo
      if (isCapturePassword) {
        await vsleadsService.updatePasswordCapture(videoIdToUse, backendData);
      } else {
        await vsleadsService.updateDataCapture(videoIdToUse, backendData);
      }

      toast({
        title: translateInterfaceText("Configuração salva!"),
        description: translateInterfaceText("As alterações foram salvas com sucesso."),
      });

      navigate(`/vsleads?tab=${redirectTab}`);
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      toast({
        title: translateInterfaceText("Erro ao salvar"),
        description: translateInterfaceText("Não foi possível salvar as configurações."),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="h-full">
      {/* ESTRUTURA PRINCIPAL: 2 COLUNAS */}
      <div className="flex flex-col lg:flex-row gap-8 h-full">

        {/* COLUNA ESQUERDA: Editor + Tabs (com scroll) */}
        <div className="flex-1 lg:max-w-[780px] overflow-y-auto pr-2">
          <div className="space-y-6 pb-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              {/* Tabs alinhadas à esquerda */}
              <TabsList className="flex flex-wrap gap-3 mb-8">
                <TabsTrigger value="content">{translateInterfaceText("Conteúdo")}</TabsTrigger>
                <TabsTrigger value="layout">{translateInterfaceText("Layout")}</TabsTrigger>
                <TabsTrigger value="fields">{translateInterfaceText("Campos")}</TabsTrigger>
                <TabsTrigger value="style">{translateInterfaceText("Estilo")}</TabsTrigger>
                {!isCapturePassword && <TabsTrigger value="timer">{translateInterfaceText("Temporizador")}</TabsTrigger>}
              </TabsList>

              {/* Conteúdo das tabs */}
              <div className="mt-8 space-y-8">
                <TabsContent value="content" className="mt-0 space-y-8">
                  <ContentTab form={form} isEditMode={isEditMode} />
                </TabsContent>

                <TabsContent value="layout" className="mt-0 space-y-8">
                  <LayoutConfigTab form={form} isCapturePassword={isCapturePassword} />
                </TabsContent>

                <TabsContent value="fields" className="mt-0 space-y-8">
                  <FormFieldsTab form={form} isCapturePassword={isCapturePassword} />
                </TabsContent>

                <TabsContent value="style" className="mt-0 space-y-8">
                  <VisualStyleTab form={form} isCapturePassword={isCapturePassword} />
                </TabsContent>

                {!isCapturePassword && (
                  <TabsContent value="timer" className="mt-0 space-y-8">
                    <TimingTab form={form} />
                  </TabsContent>
                )}
              </div>
            </Tabs>

            {/* BOTÕES DE AÇÃO */}
            <div className="flex gap-3 justify-end pb-6">
              <Button
                onClick={form.handleSubmit(handleSave)}
                className="gap-2 h-11 px-6 rounded-[12px] bg-gradient-to-b from-[#2D72FF] to-[#1B52CC] shadow-[0_3px_6px_rgba(45,114,255,0.25)] hover:brightness-110 hover:-translate-y-0.5 transition-all duration-200"
              >
                <Save className="h-4 w-4" />
                {translateInterfaceText("Salvar Configuração")}</Button>
            </div>
          </div>
        </div>

        {/* COLUNA DIREITA: Preview Sticky (flexível, ocupa o resto) */}
        <div className="flex-1 lg:min-w-[520px]">
          <div className="lg:sticky lg:top-6">
            <EmailCapturePreview
              config={config}
              videoUrl={videoUrl}
              isLoading={isLoading}
              isCapturePassword={isCapturePassword}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
